import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RecentRecommendationCard = ({ recommendation, onView, onCopy, timeCategory }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency?.toLowerCase()) {
      case 'emergency':
        return 'text-destructive bg-destructive/10';
      case 'urgent':
        return 'text-warning bg-warning/10';
      case 'routine':
        return 'text-success bg-success/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const handleViewClick = () => {
    onView?.(recommendation?.id);
  };

  return (
    <div className="clinical-card p-4 hover:shadow-md transition-clinical">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="User" size={16} color="var(--color-muted-foreground)" />
            <span className="text-sm font-medium text-foreground">
              Patient: {recommendation?.patientAge || recommendation?.patient_age}y {recommendation?.patientGender || recommendation?.patient_gender}
            </span>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <Icon name="MapPin" size={14} color="var(--color-muted-foreground)" />
            <span className="text-xs text-muted-foreground capitalize">
              {recommendation?.bodyRegion || recommendation?.body_region}
            </span>
          </div>
          {timeCategory && (
            <div className="flex items-center gap-1 mt-1">
              <Icon name="Clock" size={12} color="var(--color-muted-foreground)" />
              <span className="text-xs text-muted-foreground">
                {timeCategory}
              </span>
            </div>
          )}
        </div>
        {recommendation?.urgency && (
          <span className={`px-2 py-1 rounded text-xs font-medium ${getUrgencyColor(recommendation?.urgency)}`}>
            {recommendation?.urgency}
          </span>
        )}
      </div>

      <div className="mb-3">
        <p className="text-sm text-muted-foreground line-clamp-2">
          {recommendation?.clinicalPresentation || recommendation?.clinical_presentation}
        </p>
      </div>

      <div className="flex items-center gap-2 mb-3 p-2 bg-primary/10 rounded-md">
        <Icon name="Stethoscope" size={16} color="var(--color-primary)" />
        <span className="text-sm font-medium text-primary">
          {recommendation?.recommendedModality || recommendation?.recommended_modality}
        </span>
      </div>

      {isExpanded && (
        <div className="mb-3 p-3 bg-muted rounded-md animate-fade-in">
          <p className="text-xs text-muted-foreground mb-2">
            <span className="font-semibold">Reasoning:</span> {recommendation?.reasoning || 'No reasoning provided'}
          </p>
          {recommendation?.confidenceScore && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground">Confidence:</span>
              <div className="flex-1 bg-background rounded-full h-2">
                <div 
                  className="bg-success h-2 rounded-full" 
                  style={{ width: `${(recommendation?.confidenceScore || recommendation?.confidence_score) * 100}%` }}
                />
              </div>
              <span className="text-xs font-medium text-success">
                {((recommendation?.confidenceScore || recommendation?.confidence_score) * 100)?.toFixed(0)}%
              </span>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          size="sm"
          iconName="Eye"
          iconPosition="left"
          onClick={handleViewClick}
          className="flex-1"
        >
          View Full Details
        </Button>
        <Button
          variant="ghost"
          size="sm"
          iconName="Copy"
          onClick={() => onCopy?.(recommendation?.id)}
          title="Copy recommendation"
        />
        <Button
          variant="ghost"
          size="sm"
          iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
          onClick={() => setIsExpanded(!isExpanded)}
          title={isExpanded ? "Show less" : "Show more"}
        />
      </div>

      <div className="mt-2 pt-2 border-t border-border">
        <span className="text-xs text-muted-foreground">
          {formatDate(recommendation?.createdAt || recommendation?.created_at)}
        </span>
      </div>
    </div>
  );
};

export default RecentRecommendationCard;